import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HORIZONTE_DA_SERIE_SEMANAS,
  PASSO_DA_GRADE_MINUTOS,
  ROTULO_TIPO_MARCACAO,
  TIPOS_OPERANTES,
  zNovaMarcacao,
  type ConsultaDeDisponibilidade,
  type CorDeCard,
  type GradeDoDia,
  type ResultadoDeDisponibilidade,
  type TipoOperante,
} from '@arena/shared/schemas';
import {
  adicionarDias,
  dataValida,
  diaDaSemana,
  horaDe,
  horaNoRelogio,
  minutosNaGrade,
  noCalendario,
} from '@arena/shared/tempo';
import { agendaApi } from '../api/agenda.js';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Escolhas, Formulario } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import {
  CORES_DO_CARD,
  NOMES_CURTOS_DOS_DIAS,
  PRESETS_DE_PAGAMENTO,
  dataPorExtenso,
  horaCurta,
  paraCentavos,
  rotuloDeDuracao,
  type ChaveDePagamento,
} from './formatos.js';
import { janelaDaGrade, trechosOcupados } from './layout.js';
import { SeletorDeHorario } from './SeletorDeHorario.jsx';
import { useAtrasado } from './useAtrasado.js';

/**
 * `#m-reserva` — nova reserva.
 *
 * O formulário valida com `zNovaMarcacao`, o MESMO schema que o
 * servidor usa no corpo da requisição. Não há uma segunda definição de
 * "locação precisa de responsável" aqui que possa divergir da de lá.
 *
 * A verificação de conflito acontece enquanto a pessoa preenche — o
 * bloco vermelho "Conflito. Quadra 1 já tem marcação às 19:00" ou o
 * verde de confirmação do protótipo. É conforto: a garantia é a
 * constraint no banco, e o rodapé diz isso ("A agenda revalida conflito
 * ao salvar").
 *
 * O horário se escolhe com um clique (`SeletorDeHorario`), com o que já
 * está ocupado na quadra riscado — a grade do dia escolhido vem do mesmo
 * cache da agenda.
 *
 * Data e hora do formulário são as DA GRADE: na arena que fecha às 02:00,
 * a 01:00 escolhida na grade de sábado é "25:00" de sábado. O pedido sai
 * em calendário — domingo, 01:00 —, e a repetição semanal acompanha: a
 * noite de sábado que vira domingo repete aos domingos de madrugada.
 *
 * Ficou de fora, de propósito, a confirmação automática do protótipo:
 * ela depende do documento 15 e da decisão P7 (API oficial ou disparo
 * pela recepção). Uma caixa marcada que não envia nada seria pior que
 * nenhuma.
 */

const DURACOES = [30, 60, 90, 120, 150, 180, 240];

export function ModalDeReserva({
  grade,
  inicial,
  onFechar,
}: {
  grade: GradeDoDia;
  inicial: { quadraId?: string | undefined; hora?: string | undefined };
  onFechar: () => void;
}) {
  const avisar = useToast();
  const cliente = useQueryClient();

  const janela = janelaDaGrade(grade.horarioFuncionamento);
  const inicios = opcoesDeInicio(janela.abertura, janela.fechamento);
  const quadraInicial = grade.quadras.some((q) => q.id === inicial.quadraId)
    ? (inicial.quadraId as string)
    : (grade.quadras[0]?.id ?? '');

  const [tipo, setTipo] = useState<TipoOperante>('LocacaoAvulsa');
  const [quadraId, setQuadraId] = useState(quadraInicial);
  const [data, setData] = useState(grade.data);
  const [inicio, setInicio] = useState(
    inicial.hora && inicios.includes(inicial.hora) ? inicial.hora : (inicios[0] ?? '07:00'),
  );
  const [duracao, setDuracao] = useState(60);
  const [responsavel, setResponsavel] = useState('');
  const [telefone, setTelefone] = useState('');
  const [pessoas, setPessoas] = useState('');
  const [valor, setValor] = useState('');
  const [pagamento, setPagamento] = useState<ChaveDePagamento | ''>('');
  const [titulo, setTitulo] = useState('');
  const [observacao, setObservacao] = useState('');
  const [cor, setCor] = useState<CorDeCard | 'padrao'>('padrao');
  const [repetir, setRepetir] = useState(false);
  const [dias, setDias] = useState<number[]>([]);
  const [ate, setAte] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  // O calendário do que se escolheu na grade. Começar na madrugada é
  // começar no dia seguinte: a data anda um dia, e os dias da repetição
  // e o "até" andam junto com ela.
  const noCal = dataValida(data) ? noCalendario(data, inicio) : { data, hora: inicio };
  const viraODia = noCal.data !== data;
  const diasDaRepeticao = dias.length > 0 ? dias : dataValida(data) ? [diaDaSemana(data)] : [];
  const recorrencia = repetir
    ? {
        diasSemana: viraODia ? diasDaRepeticao.map((d) => (d + 1) % 7) : diasDaRepeticao,
        ...(ate && dataValida(ate) ? { ate: viraODia ? adicionarDias(ate, 1) : ate } : {}),
      }
    : undefined;

  // --- os horários da quadra no dia escolhido ----------------------------
  // O dia da página já está na mão; outro dia vem pelo mesmo cache da agenda.
  const outroDia = data !== grade.data && dataValida(data);
  const gradeDeOutroDia = useQuery({
    queryKey: ['agenda', 'grade', data],
    queryFn: () => agendaApi.grade(data),
    enabled: outroDia,
  });
  const marcacoesDoDia = outroDia ? (gradeDeOutroDia.data?.marcacoes ?? []) : grade.marcacoes;
  const ocupados = dataValida(data)
    ? trechosOcupados(marcacoesDoDia, quadraId, data, grade.fuso)
    : [];
  const agora = dataValida(data) ? minutosNaGrade(new Date(), data, grade.fuso) : -Infinity;

  // --- verificação ao vivo ---------------------------------------------
  const consulta: ConsultaDeDisponibilidade = useAtrasado(
    {
      quadraId,
      data: noCal.data,
      inicio: noCal.hora,
      duracaoMinutos: duracao,
      ...(recorrencia ? { recorrencia } : {}),
      // 10 · turmas — com o tipo, a resposta inclui a alocação fixa (P1):
      // locação avulsa no horário reservado já aparece recusada aqui.
      tipo,
    },
    350,
  );
  const disponibilidade = useQuery({
    queryKey: ['agenda', 'disponibilidade', consulta],
    queryFn: () => agendaApi.disponibilidade(consulta),
    enabled:
      Boolean(consulta.quadraId) &&
      dataValida(consulta.data) &&
      (!consulta.recorrencia || consulta.recorrencia.diasSemana.length > 0),
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });

  // --- gravação --------------------------------------------------------
  const criar = useMutation({
    mutationFn: agendaApi.criar,
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['agenda'] }),
  });

  function montarPedido(): unknown {
    const base = {
      tipo,
      quadraId,
      data: noCal.data,
      inicio: noCal.hora,
      duracaoMinutos: duracao,
      cor: cor === 'padrao' ? null : cor,
      ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
      ...(recorrencia ? { recorrencia } : {}),
    };
    if (tipo === 'Bloqueio') return { ...base, titulo: titulo.trim() };

    const preset = PRESETS_DE_PAGAMENTO.find((p) => p.chave === pagamento);
    const centavos = valor.trim() ? paraCentavos(valor) : null;
    return {
      ...base,
      // Evento exige nome; mandar vazio deixa o schema dar a mensagem certa.
      ...(titulo.trim() || tipo === 'Evento' ? { titulo: titulo.trim() } : {}),
      ...(responsavel.trim() || tipo === 'LocacaoAvulsa'
        ? { responsavelNome: responsavel.trim() }
        : {}),
      ...(telefone.trim() ? { responsavelTelefone: telefone.trim() } : {}),
      ...(pessoas.trim() ? { pessoas: Number(pessoas) } : {}),
      ...(centavos !== null ? { valor: centavos } : {}),
      ...(preset ? { formaPagamento: preset.forma, situacaoPagamento: preset.situacao } : {}),
    };
  }

  async function salvar() {
    setErros({});
    setErroGeral(null);

    if (valor.trim() && paraCentavos(valor) === null) {
      setErros({ valor: 'Valor inválido — use 120 ou 120,50.' });
      return;
    }

    const pedido = zNovaMarcacao.safeParse(montarPedido());
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }

    try {
      const criada = await criar.mutateAsync(pedido.data);
      const quadra = grade.quadras.find((q) => q.id === quadraId)?.nome ?? 'quadra';
      const hora = horaCurta(horaNoRelogio(inicio));
      avisar(
        criada.serie
          ? `Série criada: ${criada.serie.ocorrencias} datas na ${quadra} às ${hora}.`
          : `Reserva criada na ${quadra} às ${hora}.`,
      );
      onFechar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  const alternarDia = (dia: number) =>
    setDias((atuais) => {
      const base = atuais.length > 0 ? atuais : diasDaRepeticao;
      return base.includes(dia) ? base.filter((d) => d !== dia) : [...base, dia].sort();
    });

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Nova reserva"
      descricao="Locação avulsa, evento ou bloqueio."
      rodape={
        <>
          <span className="left">A agenda revalida conflito ao salvar</span>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void salvar()} disabled={criar.isPending}>
            {criar.isPending ? 'Salvando…' : repetir ? 'Salvar série' : 'Salvar reserva'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        <Campo
          rotulo="Tipo de marcação"
          ajuda="Turma fixa nasce na tela de Turmas; aula experimental, a partir do pedido do site."
          erro={erros['tipo']}
        >
          <Escolhas
            aria-label="Tipo de marcação"
            opcoes={TIPOS_OPERANTES.map((t) => ({ valor: t, rotulo: ROTULO_TIPO_MARCACAO[t] }))}
            valor={tipo}
            onChange={setTipo}
          />
        </Campo>

        <Formulario colunas={3} onSubmit={(e) => e.preventDefault()}>
          <Campo rotulo="Quadra" erro={erros['quadraId']}>
            <select value={quadraId} onChange={(e) => setQuadraId(e.target.value)}>
              {grade.quadras.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.nome} · {q.superficie}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Data" erro={erros['data']}>
            <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
          </Campo>
          <Campo rotulo="Duração" erro={erros['duracaoMinutos']}>
            <select value={duracao} onChange={(e) => setDuracao(Number(e.target.value))}>
              {DURACOES.map((m) => (
                <option key={m} value={m}>
                  {rotuloDeDuracao(m)}
                </option>
              ))}
            </select>
          </Campo>
        </Formulario>

        <Campo
          rotulo={`Horário de início · ${horaCurta(horaNoRelogio(inicio))}`}
          ajuda={
            viraODia && dataValida(noCal.data)
              ? `Madrugada: a reserva fica em ${dataPorExtenso(noCal.data)}, às ${noCal.hora} — na grade deste dia.`
              : 'Riscado: ocupado nesta quadra com essa duração.'
          }
          erro={erros['inicio']}
        >
          <SeletorDeHorario
            aria-label="Horário de início"
            janela={janela}
            valor={inicio}
            duracaoMinutos={duracao}
            ocupados={ocupados}
            agora={agora}
            onChange={setInicio}
          />
        </Campo>

        <div aria-live="polite">
          <AvisoDeDisponibilidade resultado={disponibilidade.data} />
        </div>

        <label className="consent">
          <input
            className="ck"
            type="checkbox"
            checked={repetir}
            onChange={(e) => setRepetir(e.target.checked)}
          />
          <span>
            Repetir toda semana
            <small>
              As próximas {HORIZONTE_DA_SERIE_SEMANAS} semanas entram na grade agora; ao mover uma
              data depois, o sistema pergunta se vale só para ela ou para toda a série.
            </small>
          </span>
        </label>

        {repetir && (
          <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
            <Campo rotulo="Dias da semana" erro={erros['recorrencia.diasSemana']}>
              <Escolhas
                aria-label="Dias da semana"
                opcoes={NOMES_CURTOS_DOS_DIAS.map((nome, dia) => ({
                  valor: String(dia),
                  rotulo: nome,
                }))}
                valor={diasDaRepeticao.map(String)}
                onChange={(dia) => alternarDia(Number(dia))}
              />
            </Campo>
            <Campo
              rotulo="Até"
              ajuda="Em branco, a série não tem fim."
              erro={erros['recorrencia.ate']}
            >
              <input type="date" value={ate} min={data} onChange={(e) => setAte(e.target.value)} />
            </Campo>
          </Formulario>
        )}

        {tipo === 'Bloqueio' ? (
          <Campo rotulo="Motivo do bloqueio" erro={erros['titulo']}>
            <input
              placeholder="Manutenção da rede"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
            />
          </Campo>
        ) : (
          <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
            {tipo === 'Evento' ? (
              <Campo rotulo="Nome do evento" erro={erros['titulo']}>
                <input
                  placeholder="Torneio de duplas"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                />
              </Campo>
            ) : null}
            <Campo
              rotulo={tipo === 'Evento' ? 'Responsável (opcional)' : 'Responsável'}
              erro={erros['responsavelNome']}
            >
              <input
                placeholder="Quem reservou"
                value={responsavel}
                onChange={(e) => setResponsavel(e.target.value)}
              />
            </Campo>
            <Campo rotulo="Telefone" ajuda="Opcional. Com DDD." erro={erros['responsavelTelefone']}>
              <input
                inputMode="tel"
                placeholder="(62) 9 9999-9999"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
              />
            </Campo>
            <Campo rotulo="Pessoas" erro={erros['pessoas']}>
              <input
                type="number"
                min={1}
                value={pessoas}
                onChange={(e) => setPessoas(e.target.value)}
              />
            </Campo>
            <Campo rotulo="Valor" erro={erros['valor']}>
              <input
                inputMode="decimal"
                placeholder="R$ 120,00"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
              />
            </Campo>
            <Campo rotulo="Pagamento" erro={erros['situacaoPagamento']}>
              <select
                value={pagamento}
                onChange={(e) => setPagamento(e.target.value as ChaveDePagamento | '')}
              >
                <option value="">Ainda não definido</option>
                {PRESETS_DE_PAGAMENTO.map((p) => (
                  <option key={p.chave} value={p.chave}>
                    {p.rotulo}
                  </option>
                ))}
              </select>
            </Campo>
          </Formulario>
        )}

        <Campo
          rotulo="Cor do card"
          ajuda="Como no Google Agenda: a cor padrão vem da quadra e pode ser sobrescrita por tipo de evento."
        >
          <div className="choices" role="group" aria-label="Cor do card">
            {CORES_DO_CARD.map((opcao) => (
              <button
                key={opcao.valor}
                type="button"
                className="choice"
                aria-pressed={cor === opcao.valor}
                style={{ borderColor: opcao.token }}
                onClick={() => setCor(opcao.valor)}
              >
                {opcao.rotulo}
              </button>
            ))}
          </div>
        </Campo>

        <Campo rotulo="Observação" ajuda="Opcional." erro={erros['observacao']}>
          <input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
        </Campo>

        {erroGeral && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erroGeral}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}

/**
 * O bloco de `#m-reserva` que responde enquanto se preenche. Verde,
 * vermelho, ou nada — "carregando" piscando a cada tecla só distrairia.
 */
export function AvisoDeDisponibilidade({
  resultado,
}: {
  resultado: ResultadoDeDisponibilidade | undefined;
}) {
  if (!resultado) return null;

  if (resultado.livre) {
    return (
      <div className="aviso livre">
        <Icone nome="check" />
        <div>
          {resultado.ocorrencias > 1
            ? `As ${resultado.ocorrencias} datas estão livres nesta quadra. A série pode ser salva.`
            : 'Horário livre nesta quadra. A reserva pode ser salva.'}
        </div>
      </div>
    );
  }

  return (
    <div className="aviso conflito">
      <Icone nome="info" />
      <div>
        {resultado.conflitos.length > 0 && <b>Conflito. </b>}
        {resultado.mensagem}
      </div>
    </div>
  );
}

/** Os inícios possíveis: de meia em meia hora, até meia hora antes de fechar. */
export function opcoesDeInicio(abertura: number, fechamento: number): string[] {
  const opcoes: string[] = [];
  for (let m = abertura; m + PASSO_DA_GRADE_MINUTOS <= fechamento; m += PASSO_DA_GRADE_MINUTOS) {
    opcoes.push(horaDe(m));
  }
  return opcoes;
}

/**
 * O primeiro erro de cada campo, pelo caminho do zod ("recorrencia.ate").
 * Tipo estrutural: o front não depende do zod diretamente — ele chega
 * pelos schemas de @arena/shared.
 */
export function errosPorCampo(
  issues: readonly { path: readonly (string | number)[]; message: string }[],
): Record<string, string> {
  const erros: Record<string, string> = {};
  for (const issue of issues) {
    const campo = issue.path.join('.') || '_';
    erros[campo] ??= issue.message;
  }
  return erros;
}

export function primeiraMensagem(campos: Record<string, string[]>): Record<string, string> {
  const erros: Record<string, string> = {};
  for (const [campo, mensagens] of Object.entries(campos)) {
    if (mensagens[0]) erros[campo] = mensagens[0];
  }
  return erros;
}
